import { useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';

const Terms = () => {
  useEffect(() => {
    document.title = 'Terms of Service | Diamond Club Matchmaking';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Terms of Service for Diamond Club Matchmaking. Read our eligibility requirements, service terms, and policies.');
    }
  }, []);

  return (
    <Layout>
      <section className="min-h-screen bg-black-deep grain-overlay">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          {/* Page Title */}
          <div className="text-center mb-12">
            <h1 className="font-serif text-display-sm md:text-display text-cream mb-2">
              Terms of Service
            </h1>
            <p className="font-serif text-xl text-gold mb-4">
              Diamond Club Matchmaking
            </p>
            <p className="font-sans text-cream-muted text-sm">
              Effective Date: January 1, 2025
            </p>
          </div>
          
          {/* Introduction */}
          <div className="mb-12">
            <p className="font-sans text-cream-muted text-base leading-relaxed">
              Welcome to Diamond Club Matchmaking ("Diamond Club," "we," "us," or "our"). By accessing or using our website, services, events, or member portals (collectively, the "Services"), you agree to be bound by these Terms of Service ("Terms").
            </p>
            <p className="font-sans text-cream-muted text-base leading-relaxed mt-4">
              If you do not agree to these Terms, please do not use our Services.
            </p>
          </div>
          
          {/* Sections */}
          <div className="space-y-10">
            {/* Section 1 */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                1. Eligibility
              </h2>
              <p className="font-sans text-cream-muted text-base leading-relaxed">
                You must be at least 18 years of age to use Diamond Club services. By using the Services, you represent and warrant that you meet this requirement and have the legal capacity to enter into this agreement.
              </p>
            </div>

            {/* Section 2 */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                2. Nature of Services
              </h2>
              <p className="font-sans text-cream-muted text-base leading-relaxed mb-4">
                Diamond Club provides curated social introductions, matchmaking services, events, and/or access to member portals depending on the membership or service purchased.
              </p>
              <ul className="font-sans text-cream-muted text-base leading-relaxed space-y-2 ml-6">
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>We do not guarantee romantic outcomes, compatibility, or relationship success.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Introductions are not endorsements.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Participation is voluntary and at your own discretion.</span>
                </li>
              </ul>
            </div>

            {/* Section 3 */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                3. Memberships, Fees & Payments
              </h2>
              <ul className="font-sans text-cream-muted text-base leading-relaxed space-y-2 ml-6">
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>All fees are disclosed prior to purchase.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Memberships may be recurring or fixed-term depending on the plan selected.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>All sales are final. No refunds, unless explicitly stated in writing.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Diamond Club reserves the right to change pricing or offerings at any time.</span>
                </li>
              </ul>
            </div>

            {/* Section 4 */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                4. No Professional Advice
              </h2>
              <p className="font-sans text-cream-muted text-base leading-relaxed">
                Diamond Club does not provide medical, psychological, legal, or financial advice. Any coaching, guidance, or content is for informational and entertainment purposes only.
              </p>
            </div>

            {/* Section 5 */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                5. Conduct & Responsibility
              </h2>
              <p className="font-sans text-cream-muted text-base leading-relaxed mb-4">
                You agree to:
              </p>
              <ul className="font-sans text-cream-muted text-base leading-relaxed space-y-2 ml-6 mb-4">
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Represent yourself honestly</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Treat other members with respect</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Follow all applicable laws</span>
                </li>
              </ul>
              <p className="font-sans text-cream-muted text-base leading-relaxed mb-4">
                Diamond Club is not responsible for:
              </p>
              <ul className="font-sans text-cream-muted text-base leading-relaxed space-y-2 ml-6">
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Member behavior</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Off-platform interactions</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold mr-3">•</span>
                  <span>Emotional, financial, or physical outcomes</span>
                </li>
              </ul>
            </div>

            {/* Section 6 */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                6. Assumption of Risk & Release
              </h2>
              <p className="font-sans text-cream-muted text-base leading-relaxed">
                You acknowledge that meeting new people carries inherent risks. You voluntarily assume all risks associated with participation and agree to release and hold harmless Diamond Club, its owners, contractors, and affiliates from any claims, damages, or liabilities arising from your participation.
              </p>
            </div>

            {/* Section 7 */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                7. Termination
              </h2>
              <p className="font-sans text-cream-muted text-base leading-relaxed">
                Diamond Club reserves the right to suspend or terminate access at its sole discretion, without refund, if a user violates these Terms or acts in a manner inconsistent with our values or community standards.
              </p>
            </div>

            {/* Section 8 */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                8. Intellectual Property
              </h2>
              <p className="font-sans text-cream-muted text-base leading-relaxed">
                All content, branding, logos, text, images, and materials are the exclusive property of Diamond Club and may not be used, copied, or distributed without written permission.
              </p>
            </div>

            {/* Section 9 */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                9. Limitation of Liability
              </h2>
              <p className="font-sans text-cream-muted text-base leading-relaxed">
                To the fullest extent permitted by law, Diamond Club shall not be liable for indirect, incidental, special, or consequential damages arising out of your use of the Services.
              </p>
            </div>

            {/* Section 10 */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                10. Governing Law
              </h2>
              <p className="font-sans text-cream-muted text-base leading-relaxed">
                These Terms are governed by the laws of the State of Florida, without regard to conflict of law principles.
              </p>
            </div>

            {/* Section 11 */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                11. Modifications
              </h2>
              <p className="font-sans text-cream-muted text-base leading-relaxed">
                We may update these Terms at any time. Continued use of the Services constitutes acceptance of the updated Terms.
              </p>
            </div>

            {/* Section 12 */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                12. SMS/Text Message Terms
              </h2>
              <p className="font-sans text-cream-muted text-base leading-relaxed">
                By providing your mobile number, you consent to receive automated text messages from Diamond Club Matchmaking at the number provided. Consent is not a condition of purchase. You certify that you are the account holder or have account holder permission. Standard message and data rates may apply. For support, text HELP or call <a href="tel:+19548004548" className="text-gold hover:text-gold-light transition-colors">954-800-4548</a>.
              </p>
            </div>

            {/* Section 13 */}
            <div>
              <h2 className="font-serif text-xl text-cream mb-4">
                13. Contact
              </h2>
              <p className="font-sans text-cream-muted text-base leading-relaxed">
                Diamond Club Matchmaking
              </p>
              <p className="font-sans text-cream-muted text-base leading-relaxed">
                Email: <a href="mailto:Info@diamondclubmatchmaking.com" className="text-gold hover:text-gold-light transition-colors">Info@diamondclubmatchmaking.com</a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Terms;
